import axios from "axios";

import { getQueryParam } from "../utils/queryStringHelper";
import {
  getSessionStorage,
  setSessionStorage,
  removeSessionStorage,
} from "../utils/sessionStorageHelper";
import { API } from "../config/api";
import { STORAGE_KEYS } from "../network";
import ErrorHandler from "../error/errorHandler";
import type { Result } from "../utils/request";

/**
 * 인증 관련 상수
 */
const AUTH_CONSTANTS = {
  GUEST_TOKEN: "GUEST_TOKEN",
  SUCCESS_STATE: "success",
  MAX_LOGIN_RETRY: 3,
  RETRY_DELAY_MS: 5000,
} as const;

export interface AuthResponse {
  requestId: string;
  state: string;
  data: UserInfo;
  compressed: boolean;
}

export interface UserInfo {
  sessionKey: string;
  playerId: string;
  playerName: string;
  playerStatus: number;
  refreshToken?: string;
  assets: {
    walletType: string;
    balance: number;
  }[];
}

/**
 * 인증용 POST 요청 헬퍼 함수
 * @param url 요청 URL
 * @param body 요청 바디
 * @returns Result 타입의 응답
 */
async function authRequestPost<T>(
  url: string,
  body?: unknown
): Promise<Result<T>> {
  try {
    const response = await axios.post<T>(url, body, {
      headers: { "Content-Type": "application/json" },
    });
    return { ok: true, data: response.data };
  } catch (error) {
    console.error("[Auth] Request failed:", error);
    return { ok: false, error };
  }
}

/**
 * 로그인 시도 결과
 */
interface LoginAttemptResult {
  success: boolean;
  userInfo?: UserInfo;
  responseData?: any;
}

/**
 * 로그인 시도 및 사용자 정보 저장
 * @param token 인증 토큰
 * @returns 로그인 시도 결과
 */
async function attemptLogin(token: string): Promise<LoginAttemptResult> {
  const response = await authRequestPost<AuthResponse>(API.auth.TOKEN_LOGIN, {
    token,
  });

  if (response.ok && response.data.state === AUTH_CONSTANTS.SUCCESS_STATE) {
    const userInfo = response.data.data;
    setSessionStorage(STORAGE_KEYS.USER_INFO, userInfo);
    return { success: true, userInfo };
  }

  return {
    success: false,
    responseData: response.ok ? response.data : response.error,
  };
}

/**
 * 로그인 시도 내부 로직
 * - 1단계: 쿼리 토큰 (없으면 게스트 토큰)
 * - 2단계: 리프레시 토큰 (있으면)
 * @throws {Error} 모든 토큰으로 로그인 실패 시 (마지막 응답 데이터 포함)
 */
async function attemptLoginProcess(): Promise<UserInfo> {
  const queryToken = getQueryParam("token");
  const cachedUserInfo = getSessionStorage<UserInfo>(STORAGE_KEYS.USER_INFO);

  const tokens: string[] = [];
  tokens.push(queryToken || AUTH_CONSTANTS.GUEST_TOKEN);

  if (cachedUserInfo?.refreshToken) {
    tokens.push(cachedUserInfo.refreshToken);
  }

  let lastResponseData: any = null;
  for (const token of tokens) {
    const result = await attemptLogin(token);
    if (result.success && result.userInfo) {
      return result.userInfo;
    }
    lastResponseData = result.responseData;
  }

  const error: any = new Error("LOGIN_FAILED_ALL_ATTEMPTS_FAILED");
  error.lastResponseData = lastResponseData;
  throw error;
}

/**
 * 로그인
 * - 1단계: 쿼리 토큰 (없으면 게스트 토큰)
 * - 2단계: 리프레시 토큰 (있으면)
 * - 전체 프로세스가 실패하면 최대 3회까지 재시도
 * @throws {Error} 모든 재시도 실패 시
 */
export async function login(): Promise<UserInfo> {
  let lastError: any = null;

  for (let attempt = 1; attempt <= AUTH_CONSTANTS.MAX_LOGIN_RETRY; attempt++) {
    try {
      const userInfo = await attemptLoginProcess();
      if (attempt > 1) {
        console.log(`[Auth] 로그인 성공 (${attempt}회차 시도)`);
      }
      return userInfo;
    } catch (error) {
      lastError = error;
      console.warn(
        `[Auth] 로그인 실패 (${attempt}/${AUTH_CONSTANTS.MAX_LOGIN_RETRY}):`,
        error
      );

      if (attempt < AUTH_CONSTANTS.MAX_LOGIN_RETRY) {
        await new Promise((resolve) =>
          setTimeout(resolve, AUTH_CONSTANTS.RETRY_DELAY_MS)
        );
      }
    }
  }

  const errorMessage = `LOGIN_FAILED_ALL_ATTEMPTS_FAILED: ${AUTH_CONSTANTS.MAX_LOGIN_RETRY}회 재시도 모두 실패`;

  ErrorHandler.send({
    message: errorMessage,
    data: lastError?.lastResponseData,
  });

  throw new Error(errorMessage);
}

/**
 * 로그아웃
 * - 서버에 로그아웃 요청을 보내고 로컬 세션 정보를 정리
 * - 서버 요청 실패 시에도 로컬 정보는 삭제됨
 */
export async function logout(): Promise<void> {
  const userInfo = getSessionStorage<UserInfo>(STORAGE_KEYS.USER_INFO);

  if (userInfo) {
    const response = await authRequestPost(API.auth.LOGOUT, {
      sessionId: userInfo.sessionKey,
    });

    if (!response.ok) {
      console.warn(
        "[Auth] 로그아웃 요청 실패, 로컬 세션은 삭제됩니다:",
        response.error
      );
    }
  }

  removeSessionStorage(STORAGE_KEYS.USER_INFO);
}
